import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import css from './HeaderMenu.module.scss';
import { handleClick } from '../../widgets/menu/helpers';
import { svgSprite } from '../../hybrid/wrappers';
import { LangContext } from '../../common/decorators/Language';
import RegionAndCurrencySwitcher from '../../widgets/regionAndCurrencySwitcher/RegionAndCurrencySwitcher/RegionAndCurrencySwitcher';
import classNames from 'classnames';
import ReactDOM from 'react-dom';
import { useWindowDimensions } from '../../common/utils/windowDimensions';
import Burger from '../../widgets/burger/Burger';
import { addedMenuItems, checkBrowser } from '../../common/utils/utils';
import UserContext, { UserProfileContext } from '../../common/utils/getContext';
import { getItemMenu } from './initData';

const MenuItem = ({ menuItem }) => {
    const [submenu, setSubmenu] = useState(false);
    const itemRef = useRef();
    const openSub = (item) => {
        if (submenu?.asLink === item?.asLink) {
            setSubmenu(false);
        } else {
            setSubmenu(menuItem);
        }
    };

    // стилі підменю для положення в DOM
    const [submenuStyles, setSubmenuStyles] = useState({});

    const closeSub = () => {
        setSubmenu(false);
    };

    const isActive = useMemo(() => submenu && submenu?._id === menuItem?._id, [submenu, menuItem]);

    // створюєм портал для підменю
    const submenuPortalCreator = () => {
        // позиція елементу з якого відкривається підменю
        const itemPosition = itemRef?.current?.getBoundingClientRect();
        const { left, bottom, width } = itemPosition || {};
        setSubmenuStyles({
            left: `${checkBrowser(left) - width}px`,
            top: `${checkBrowser(bottom) + 16}px`
        });
    };
    return (
        <p
            onClick={(e) => {
                handleClick(e, menuItem, openSub, closeSub);
                submenuPortalCreator();
            }}
            ref={itemRef}
            className={classNames(css.menuItem, { [css.menuItemActive]: isActive }, { transformText: menuItem?.type !== 'part' })}
            data-after={menuItem.title}
            data-id={menuItem?._id}
        >
            <span>{menuItem.title}</span>
            {menuItem?.type === 'part' && menuItem?.submenu?.length > 0 ? (
                <div className={classNames(css.menuItemAngle, { [css.menuItemAngleRotate]: isActive })}>
                    {svgSprite('angle-down', { className: css.menuItemAngleSvg })}
                </div>
            ) : null}
            {submenu && menuItem?.submenu?.length > 0
                ? ReactDOM.createPortal(
                      <div style={submenuStyles} id={menuItem?._id} onMouseLeave={() => closeSub()} className={css.submenu}>
                          {submenu?.submenu?.map((item) => (
                              <p
                                  onClick={(e) => {
                                      handleClick(e, item, () => {}, closeSub);
                                  }}
                                  className={css.submenuItem}
                                  key={item?._id}
                              >
                                  {item?.title}
                              </p>
                          ))}
                      </div>,
                      document.body
                  )
                : null}
        </p>
    );
};

const HeaderMenu = () => {
    const { roles = [], profileData } = useContext(UserProfileContext);
    const { lang, menu } = useContext(UserContext);

    const { translation } = useContext(LangContext);
    const newOfficeItems = useMemo(() => addedMenuItems(profileData), [profileData]);
    const menus = useMemo(() => [...menu?.office, ...newOfficeItems?.office], [menu, newOfficeItems]);
    const itemMenu = useMemo(
        () => getItemMenu(menus, roles, lang, true, translation, profileData, true),
        [menus, roles, lang, translation, profileData]
    );

    const [hiddenItems, setHiddenItems] = useState([]);
    const hiddenItemsForModal = useMemo(() => {
        const items = hiddenItems.map((item) => {
            const finded = itemMenu.find((it) => it._id === item.getAttribute('data-id'));
            return finded;
        });
        return items;
    }, [hiddenItems, itemMenu]);

    const menuWrapRef = useRef();
    const menuListRef = useRef();
    const switcherRef = useRef();
    const burgerRef = useRef();
    const width = useWindowDimensions();

    const mobile = width < 768;

    // логіка схову/відображення пунктів меню при різній ширині екрану
    useEffect(() => {
        if (!itemMenu?.length) return;
        // ширина всього блоку
        const { width: wrapperWidth } = menuWrapRef?.current?.getBoundingClientRect() || {};
        // ширина меню
        const { width: menuWidth } = menuListRef?.current?.getBoundingClientRect();
        // ширина віджету зміни налаштувань
        const { width: switcherWidth } = switcherRef?.current?.getBoundingClientRect();
        // ширина бургера
        const burgerWidth = 60;
        // доступний вільний простір

        let whiteSpaceWidth = wrapperWidth - menuWidth - switcherWidth - burgerWidth;
        const children = menuListRef?.current.children;
        if (!children) return;
        if (whiteSpaceWidth < 0) {
            let menuListWidth = 0;
            const hiddenItems = [];
            let allDisplayNone = false;
            for (const menuItem of children) {
                const temp = wrapperWidth - switcherWidth - menuListWidth - 60;
                // 40px відступу справа
                const itemWidth = menuItem?.getBoundingClientRect()?.width + 40;
                if (temp - itemWidth > 0 && !allDisplayNone) {
                    menuListWidth += itemWidth;
                } else {
                    hiddenItems.push(menuItem);
                    menuItem.style.display = 'none';
                    allDisplayNone = true;
                }
            }
            setHiddenItems(hiddenItems);
        } else {
            let breakLoop = false;
            const removedHiddenItems = [];
            for (const menuItem of children) {
                if (!breakLoop) {
                    const isHidden = menuItem.style.getPropertyValue('display') === 'none';
                    if (isHidden) {
                        menuItem.style.opacity = '0';
                        menuItem.style.display = 'flex';
                        // 40px відступу справа
                        const itemWidth = menuItem?.getBoundingClientRect()?.width + 40;
                        menuItem.style.opacity = '1';
                        menuItem.style.display = 'none';
                        if (whiteSpaceWidth - itemWidth > 0) {
                            removedHiddenItems.push(menuItem);
                            menuItem.style.display = 'flex';
                            whiteSpaceWidth -= itemWidth;
                        } else {
                            breakLoop = true;
                        }
                    }
                }
            }
            if (removedHiddenItems.length) {
                const removedByIds = removedHiddenItems.map((el) => el.getAttribute('data-id'));
                setHiddenItems((s) =>
                    s.filter((it) => {
                        if (removedByIds.includes(it.getAttribute('data-id'))) {
                            return false;
                        }
                        return true;
                    })
                );
            }
        }
    }, [width, itemMenu]);

    const [burgerModal, showBurgerModal] = useState(false);
    const [burgerModalStyles, setBurgerModalStyles] = useState({});

    // створюєм портал для бургера
    const burgerPortalCreator = () => {
        // позиція елементу з якого відкривається підменю
        const itemPosition = burgerRef?.current?.getBoundingClientRect();
        const { left, bottom } = itemPosition || {};
        setBurgerModalStyles({
            left: `${checkBrowser(left) - 32}px`,
            top: `${checkBrowser(bottom) + 20}px`
        });
    };

    const [burgerSubmenuStyles, setBurgerSubmenuStyles] = useState({});
    const [burgerSubmenu, showBurgerSubmenu] = useState(false);

    // створюєм портал для підменю бургера
    const burgerSubmenuPortalCreator = (event) => {
        const itemPosition = event.target.getBoundingClientRect();
        const { top, right } = itemPosition || {};
        setBurgerSubmenuStyles({
            left: `${checkBrowser(right) - 28}px`,
            top: `${checkBrowser(top) - 9}px`
        });
    };

    return (
        <>
            <div ref={menuWrapRef} className={classNames(css.menu, 'scrollbar-hidden')}>
                <div
                    ref={burgerRef}
                    style={{
                        width: hiddenItems.length ? 'auto' : '0',
                        marginRight: hiddenItems.length ? '2rem' : '0',
                        overflow: hiddenItems.length ? 'visible' : 'hidden'
                    }}
                    className={css.burger}
                >
                    <Burger
                        onOpen={() => {
                            showBurgerModal((s) => !s);
                            showBurgerSubmenu(false);
                            burgerPortalCreator();
                        }}
                    />
                </div>
                <div ref={menuListRef} className={css.menuList}>
                    {itemMenu?.map((menuItem) => (
                        <MenuItem key={menuItem?._id} menuItem={menuItem} />
                    ))}
                </div>
                <div ref={switcherRef} className={css.regionAndCurrency}>
                    <RegionAndCurrencySwitcher />
                </div>
            </div>
            {burgerModal && !mobile
                ? ReactDOM.createPortal(
                      <div style={burgerModalStyles} className={css.burgerModal}>
                          {hiddenItemsForModal.map((item) => (
                              <div
                                  key={item?._id}
                                  onClick={(e) =>
                                      handleClick(
                                          e,
                                          item,
                                          () => {
                                              showBurgerSubmenu(item);
                                              burgerSubmenuPortalCreator(e);
                                          },
                                          () => {
                                              showBurgerModal(false);
                                              showBurgerSubmenu(false);
                                          }
                                      )
                                  }
                                  className={classNames(css.burgerItem, {
                                      [css.burgerItemSelected]: item._id === burgerSubmenu?.parent
                                  })}
                              >
                                  <p>{item.title}</p>
                                  {item.submenu && item.submenu?.length ? (
                                      <div className={css.burgerItemSubmenuIcon}>
                                          {svgSprite('angle-right', { className: css.burgerItemSubmenuIconSvg })}
                                      </div>
                                  ) : null}
                              </div>
                          ))}
                      </div>,
                      document.body
                  )
                : null}
            {burgerSubmenu && !mobile
                ? ReactDOM.createPortal(
                      <div style={burgerSubmenuStyles} className={css.burgerSubmenu}>
                          {burgerSubmenu?.submenu?.map((item) => (
                              <div
                                  key={item?._id}
                                  className={css.burgerSubmenuItem}
                                  onClick={(event) =>
                                      handleClick(
                                          event,
                                          item,
                                          () => {},
                                          () => {
                                              showBurgerModal(false);
                                              showBurgerSubmenu(false);
                                          }
                                      )
                                  }
                              >
                                  {item.title}
                              </div>
                          ))}
                      </div>,
                      document.body
                  )
                : null}
        </>
    );
};

export default HeaderMenu;
